import React, { useCallback, useEffect, useState } from "react";

import { useI18nContext } from "@/hooks/useI18nContext";
import { HAS_ACCEPTED_COOKIES } from "@/shared/constants/common.constants";
import { CookiesCopiesType } from "@/shared/data/common.data";
import { ROUTE_PATHS } from "@/shared/data/routes";
import { useRouter } from "next/router";
import { parseCookies, setCookie } from "nookies";
import { ButtonSizeEnum, ButtonTypeEnum } from "../atoms/Button";
import CtaButtonGroup from "../molecules/CtaButtonGroup";

interface CookiesConsentBannerProps {}

const CookiesConsentBanner: React.FC<CookiesConsentBannerProps> = () => {
  const { locale, resources } = useI18nContext();
  const router = useRouter();
  const [hasAccepted, setHasAccepted] = useState<boolean>(true);
  const [showPrivacyButton, setShowPrivacyButton] = useState<boolean>(true);

  const cookiesCopies = resources.cookiesCopies as CookiesCopiesType;

  useEffect(() => {
    const cookies = parseCookies();
    const consent = cookies[HAS_ACCEPTED_COOKIES]
      ? JSON.parse(cookies[HAS_ACCEPTED_COOKIES])
      : false;
    const privacyPage = router.asPath === ROUTE_PATHS.LEGAL.PrivacyPolicy;
    setHasAccepted(Boolean(consent));
    setShowPrivacyButton(!privacyPage);
  }, [router.asPath]);

  const cookiesConsentHandler = () => {
    setCookie(null, HAS_ACCEPTED_COOKIES, JSON.stringify(true), {
      maxAge: 30 * 24 * 60 * 60,
      path: "/",
      // domain: IS_DEVELOPMENT_ENV ? `` : `.${ROOT_HOST}`,
    });
    setHasAccepted(true);
  };

  const renderCookieBanner = useCallback(
    () => (
      <div
        className={
          "sticky bottom-0 z-override w-full border-t border-grey-2 bg-grey-0 px-4 py-3 text-grey-4 sm:px-8 md:px-16 lg:px-24 xl:px-40"
        }
      >
        <div className="block justify-between md:flex">
          <div className="flex items-center text-body-14 md:mr-8">
            <span className="max-w-xl">{cookiesCopies?.description}</span>
          </div>
          <div className="mt-4 flex items-center whitespace-nowrap text-center md:mt-0">
            <CtaButtonGroup
              ctas={
                showPrivacyButton
                  ? [
                      {
                        type: ButtonTypeEnum.Secondary,
                        size: ButtonSizeEnum.Small,
                        href: ROUTE_PATHS.LEGAL.PrivacyPolicy,
                        text: cookiesCopies?.ctas.VIEW_PRIVACY_POLICY,
                      },
                      {
                        type: ButtonTypeEnum.Primary,
                        size: ButtonSizeEnum.Small,
                        text: cookiesCopies?.ctas.ACCEPT_COOKIES,
                        onClick: cookiesConsentHandler,
                      },
                    ]
                  : [
                      {
                        type: ButtonTypeEnum.Primary,
                        size: ButtonSizeEnum.Small,
                        text: cookiesCopies?.ctas.ACCEPT_COOKIES,
                        onClick: cookiesConsentHandler,
                      },
                    ]
              }
            />
          </div>
        </div>
      </div>
    ),
    [
      cookiesCopies?.ctas.ACCEPT_COOKIES,
      cookiesCopies?.ctas.VIEW_PRIVACY_POLICY,
      cookiesCopies?.description,
      showPrivacyButton,
    ]
  );

  if(!cookiesCopies) return null;

  return !hasAccepted ? renderCookieBanner() : null;
};

CookiesConsentBanner.displayName = "CookiesConsentBanner";

export default CookiesConsentBanner;
